<template>
  <div class="elevation-chart">
    <canvas 
      ref="chartCanvas"
      :width="canvasWidth"
      :height="canvasHeight"
      @click="onChartClick"
    ></canvas>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue'

export default {
  name: 'ElevationChart',
  props: {
    route: {
      type: Array,
      default: () => []
    },
    markers: {
      type: Array,
      default: () => []
    },
    userPosition: {
      type: Object,
      default: null
    },
    checkpoints: {
      type: Array,
      default: () => []
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 200
    }
  },
  setup(props, { emit }) {
    const chartCanvas = ref(null)
    const canvasWidth = ref(props.width)
    const canvasHeight = ref(props.height)

    // Helper function to calculate distance between two points
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371 // Earth's radius in kilometers
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLon = (lon2 - lon1) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    }

    // Convert route to elevation profile with distance
    const processRouteData = () => {
      if (!props.route || props.route.length < 2) {
        return []
      }

      const profile = []
      let cumulativeDistance = 0

      for (let i = 0; i < props.route.length; i++) {
        const point = props.route[i]

        if (i > 0) {
          const prevPoint = props.route[i - 1]
          const distance = calculateDistance(
            prevPoint.latitude,
            prevPoint.longitude,
            point.latitude,
            point.longitude
          )
          cumulativeDistance += distance
        }

        profile.push({
          distance: cumulativeDistance,
          altitude: point.altitude || 0,
          latitude: point.latitude,
          longitude: point.longitude
        })
      }

      return profile
    }

    // Draw the elevation chart
    const drawChart = () => {
      const canvas = chartCanvas.value
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      const profile = processRouteData() // <-- use route, not tracks
      
      if (profile.length < 2) return

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Chart dimensions with padding
      const padding = 40
      const chartWidth = canvas.width - 2 * padding
      const chartHeight = canvas.height - 2 * padding

      // Find min/max values
      const maxDistance = Math.max(...profile.map(p => p.distance))
      const minAltitude = Math.min(...profile.map(p => p.altitude))
      const maxAltitude = Math.max(...profile.map(p => p.altitude))
      const altitudeRange = maxAltitude - minAltitude

      // Scale functions
      const scaleX = (distance) => padding + (distance / maxDistance) * chartWidth
      const scaleY = (altitude) => padding + chartHeight - ((altitude - minAltitude) / altitudeRange) * chartHeight

      // Draw grid lines
      ctx.strokeStyle = '#e0e0e0'
      ctx.lineWidth = 1

      // Horizontal grid lines (altitude)
      const altitudeSteps = 5
      for (let i = 0; i <= altitudeSteps; i++) {
        const altitude = minAltitude + (altitudeRange / altitudeSteps) * i
        const y = scaleY(altitude)
        
        ctx.beginPath()
        ctx.moveTo(padding, y)
        ctx.lineTo(padding + chartWidth, y)
        ctx.stroke()

        // Label
        ctx.fillStyle = '#666'
        ctx.font = '10px Arial'
        ctx.textAlign = 'right'
        ctx.fillText(`${Math.round(altitude)}m`, padding - 5, y + 3)
      }

      // Vertical grid lines (distance)
      const distanceSteps = 5
      for (let i = 0; i <= distanceSteps; i++) {
        const distance = (maxDistance / distanceSteps) * i
        const x = scaleX(distance)
        
        ctx.beginPath()
        ctx.moveTo(x, padding)
        ctx.lineTo(x, padding + chartHeight)
        ctx.stroke()

        // Label
        ctx.fillStyle = '#666'
        ctx.font = '10px Arial'
        ctx.textAlign = 'center'
        ctx.fillText(`${distance.toFixed(1)}km`, x, canvas.height - 10)
      }

      // Draw elevation profile
      ctx.strokeStyle = '#2563eb'
      ctx.lineWidth = 2
      ctx.beginPath()

      profile.forEach((point, index) => {
        const x = scaleX(point.distance)
        const y = scaleY(point.altitude)

        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })

      ctx.stroke()

      // Fill area under curve
      ctx.fillStyle = 'rgba(37, 99, 235, 0.1)'
      ctx.beginPath()
      ctx.moveTo(scaleX(profile[0].distance), scaleY(minAltitude))
      
      profile.forEach(point => {
        ctx.lineTo(scaleX(point.distance), scaleY(point.altitude))
      })
      
      ctx.lineTo(scaleX(profile[profile.length - 1].distance), scaleY(minAltitude))
      ctx.closePath()
      ctx.fill()

      // Draw checkpoints
      if (props.checkpoints && props.checkpoints.length > 0) {
        props.checkpoints.forEach(checkpoint => {
          // Find closest track point to checkpoint
          let closestPoint = null
          let minDistance = Infinity

          profile.forEach(point => {
            const distance = calculateDistance(
              checkpoint.latitude || 0,
              checkpoint.longitude || 0,
              point.latitude,
              point.longitude
            )

            if (distance < minDistance) {
              minDistance = distance
              closestPoint = point
            }
          })

          if (closestPoint && minDistance < 1) { // Within 1km
            const x = scaleX(closestPoint.distance)
            const y = scaleY(closestPoint.altitude)

            // Draw checkpoint marker
            ctx.fillStyle = '#dc2626'
            ctx.beginPath()
            ctx.arc(x, y, 4, 0, 2 * Math.PI)
            ctx.fill()

            // Draw checkpoint label
            ctx.fillStyle = '#dc2626'
            ctx.font = '10px Arial'
            ctx.textAlign = 'center'
            ctx.fillText(checkpoint.number || '●', x, y - 8)
          }
        })
      }

      // Draw user position
      if (props.userPosition) {
        // Find closest track point to user position
        let closestPoint = null
        let minDistance = Infinity

        profile.forEach(point => {
          const distance = calculateDistance(
            props.userPosition.latitude,
            props.userPosition.longitude,
            point.latitude,
            point.longitude
          )

          if (distance < minDistance) {
            minDistance = distance
            closestPoint = point
          }
        })

        if (closestPoint && minDistance < 1) { // Within 1km
          const x = scaleX(closestPoint.distance)
          const y = scaleY(closestPoint.altitude)

          // Draw user marker
          ctx.fillStyle = '#10b981'
          ctx.strokeStyle = '#fff'
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.arc(x, y, 6, 0, 2 * Math.PI)
          ctx.fill()
          ctx.stroke()

          // Draw user label
          ctx.fillStyle = '#10b981'
          ctx.font = '12px Arial'
          ctx.textAlign = 'center'
          ctx.fillText('●', x, y - 12)
        }
      }
    }

    // Handle chart click
    const onChartClick = (event) => {
      // Could emit events for interactivity later
      emit('chart-click', event)
    }

    // Watch for data changes and redraw
    watch([() => props.route, () => props.userPosition, () => props.checkpoints], () => {
      nextTick(() => {
        drawChart()
      })
    }, { deep: true })

    // Draw chart on mount
    onMounted(() => {
      nextTick(() => {
        drawChart()
      })
    })

    return {
      chartCanvas,
      canvasWidth,
      canvasHeight,
      onChartClick
    }
  }
}
</script>

<style scoped>
.elevation-chart {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

canvas {
  max-width: 100%;
  height: auto;
  cursor: pointer;
}

@media (max-width: 768px) {
  .elevation-chart {
    padding: 8px;
  }
}
</style>